import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  ViewChild,
  HostListener,
  EventEmitter,
  Output,
} from '@angular/core';
import { WoundAnalysisMetrics } from 'app/core/models/wound/wound.model';
import { CommonModule } from '@angular/common';
import * as THREE from 'three';

import { ModelViewer3DComponent } from './model-viewer-3d/model-viewer-3d.component';
import { ModelViewer2DComponent } from './model-viewer-2d/model-viewer-2d.component';
import {
  MarkerControlsComponent,
  ControlAction,
} from './marker-controls/marker-controls.component';
import {
  MeasurementSidebarComponent,
  MeasurementAction,
  MeasurementTool,
} from './measurement-sidebar/measurement-sidebar.component';
import { MarkerNoteModalComponent } from './marker-note-modal/marker-note-modal.component';

import { MarkerService } from '../../../core/services/model-viewer/marker.service';
import { ViewerStateService } from '../../../core/services/model-viewer/viewer-state.service';

import {
  ViewerMode,
  RectangleData,
  MarkerData,
} from '../../../core/models/model-viewer/viewer.models';

import { Observable } from 'rxjs';
import {
  CreateWoundModelNoteRequest,
  MarkerType,
} from 'app/core/models/wound/wound-model-note.model';
import { WoundService } from 'app/core/services/wound/wound.service';
import { ActivatedRoute } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-model-viewer',
  standalone: true,
  imports: [
    CommonModule,
    ModelViewer3DComponent,
    ModelViewer2DComponent,
    MarkerControlsComponent,
    MarkerNoteModalComponent,
    TranslateModule,
    MeasurementSidebarComponent,
  ],
  templateUrl: './model-viewer.component.html',
  styleUrls: ['./model-viewer.component.scss'],
  providers: [MarkerService, ViewerStateService],
})
export class ModelViewerComponent implements OnChanges {
  @ViewChild(ModelViewer3DComponent) viewer3D?: ModelViewer3DComponent;
  @ViewChild(ModelViewer2DComponent) viewer2D?: ModelViewer2DComponent;

  @Input() modelUrl: string | null = null;
  @Input() existingNotes: any;
  @Input() allowAnnotation: boolean = true;
  @Input() hide2DToolbar: boolean = false;
  @Input() hide3DDeleteButton: boolean = false;
  @Input() woundMetrics: WoundAnalysisMetrics | null = null;
  @Input() showMeasurementSidebar: boolean = false;
  @Output() onMarkerCreated = new EventEmitter<CreateWoundModelNoteRequest>();
  @Output() onMarkerDeleted = new EventEmitter<boolean>();
  @Output() onMarkerUpdated = new EventEmitter<boolean>();

  woundId: string | null = null;

  viewerMode: ViewerMode = 'NONE';
  viewerMode$: Observable<ViewerMode>;

  isAddMarkerMode = false;
  isGizmoMode = false;
  isDrawing2DMode = false;
  isSelect2DMode = false;

  markers3D: THREE.Mesh[] = [];
  rectangles2D: RectangleData[] = [];
  markerCount = 0;

  hoverText: string | null = null;
  hoveredMarkerNote: string | null = null;
  tooltipX = 0;
  tooltipY = 0;

  showNoteModal = false;
  currentNote = '';
  isEditingMode = false;
  selectedMarkerId: string | number | null = null;
  private editingIndex: number | null = null;

  private pendingPosition: THREE.Vector3 | null = null;
  private pendingRectangle: RectangleData | null = null;

  isLoading = false;
  hasError = false;

  showControls = false;
  brightness = 1.0;
  isTextureMode = false;
  showTexture = true;
  markersVisible = true;
  isWireframe = false;
  showGrid = false;
  showAxis = false;
  showHelp = false;

  // Measurement sidebar state
  activeMeasurementTool: MeasurementTool = null;
  isMeasureDistanceMode = false;
  isMeasureAngleMode = false;
  isMeasureAreaMode = false;
  lastMeasurementResult: { type: string; value: number; unit: string; label: string; extra?: any } | null = null;

  showMetricsPanel: boolean = false;
  metrics: { label: string; value: string; unit: string; icon: string }[] = [];

  constructor(
    private markerService: MarkerService,
    private viewerStateService: ViewerStateService,
    private woundService: WoundService,
    private activatedRoute: ActivatedRoute,
  ) {
    this.viewerMode$ = this.viewerStateService.viewerMode;
    this.subscribeToServices();
    this.woundId = this.activatedRoute.snapshot.paramMap.get('woundId');
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['modelUrl']) {
      this.handleModelChange(this.modelUrl);
    }

    if (changes['woundMetrics'] && this.woundMetrics) {
      this.buildMetrics(this.woundMetrics);
    }

    if (changes['existingNotes']) {
      this.markerService.clear3DMarkers();
      this.markerService.clear2DRectangles();

      if (this.viewer3D) {
        this.markers3D.forEach((m) => this.viewer3D?.removeMarker(m));
        this.markers3D = [];
      }
      setTimeout(() => {
        this.loadExistingNotes();
      }, 100);
    }

    if (changes['showMeasurementSidebar'] && !this.showMeasurementSidebar) {
      this.clearMeasurementModes();
      this.lastMeasurementResult = null;
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 0);
  }

  private subscribeToServices(): void {
    this.viewerStateService.viewerMode.subscribe((mode) => {
      this.viewerMode = mode;
      this.updateMarkerCount();
    });

    this.viewerStateService.isAddMarkerMode.subscribe(
      (enabled) => (this.isAddMarkerMode = enabled),
    );
    this.viewerStateService.isGizmoMode.subscribe(
      (enabled) => (this.isGizmoMode = enabled),
    );

    this.viewerStateService.isDrawing2DMode.subscribe(
      (enabled) => (this.isDrawing2DMode = enabled),
    );
    this.viewerStateService.isSelect2DMode.subscribe(
      (enabled) => (this.isSelect2DMode = enabled),
    );

    this.markerService.markers3D.subscribe((markers) => {
      this.update3DMarkers(markers);
    });

    this.markerService.rectangles2D.subscribe((rectangles) => {
      this.rectangles2D = rectangles;
      this.updateMarkerCount();
    });
  }

  private loadExistingNotes(): void {
    // 1. Güvenlik Kontrolü: Obje ve içindeki notes dizisi var mı?
    if (!this.existingNotes || !Array.isArray(this.existingNotes.notes)) {
      return;
    }

    this.markerService.clear3DMarkers();
    this.markerService.clear2DRectangles();

    const apiNotes = this.existingNotes.notes || [];

    const allNotes = [...apiNotes];

    // 4. 3D Notları Çiz
    allNotes
      .filter((n) => n.markerType === MarkerType.ThreeDimensional)
      .forEach((note) => {
        try {
          const positionData = JSON.parse(note.positionData);
          const position = new THREE.Vector3(
            positionData.x,
            positionData.y,
            positionData.z,
          );
          this.markerService.add3DMarker(position, note.note, note.id);
        } catch (error) {
          console.error('3D position parse error', error);
        }
      });

    // 5. 2D Notları Çiz
    allNotes
      .filter((n) => n.markerType === MarkerType.TwoDimensional)
      .forEach((note) => {
        try {
          const rectangleData = JSON.parse(note.positionData);
          this.markerService.add2DRectangle({
            ...rectangleData,
            note: note.note,
            id: note.id,
          });
        } catch (error) {
          console.error('2D rectangle parse error', error);
        }
      });
  }
  private buildMetrics(m: WoundAnalysisMetrics): void {
    this.metrics = [
      {
        label: 'Surface Area',
        value: m.surfaceAreaCm2.toFixed(2),
        unit: 'cm²',
        icon: 'fas fa-ruler-combined',
      },
      {
        label: 'Volume',
        value: m.volumeCm3.toFixed(2),
        unit: 'cm³',
        icon: 'fas fa-cube',
      },
      {
        label: 'Perimeter',
        value: (m.boundaryLengthMm / 10).toFixed(2),
        unit: 'cm',
        icon: 'fas fa-circle-notch',
      },
    ];

    if (m.boundingBoxMm && m.boundingBoxMm.length >= 3) {
      this.metrics.push({
        label: 'Max Depth',
        value: (m.boundingBoxMm[2] / 10).toFixed(2),
        unit: 'cm',
        icon: 'fas fa-arrow-down',
      });
    }
  }

  private handleModelChange(url: string | null): void {
    // Model/tab degisiminde onceki olcum noktalarini ve sonucu temizle
    this.clearMeasurementModes();
    this.lastMeasurementResult = null;

    const newMode = this.determineViewerMode(url);

    // isLoading/hasError'ı setViewerMode'dan ÖNCE set et —
    // aksi hâlde Angular bir frame render eder: viewer görünür ama overlay henüz yok (flash)
    if (newMode === '3D' || newMode === '2D') {
      this.isLoading = true;
      this.hasError = false;
    } else {
      this.isLoading = false;
      this.hasError = false;
    }

    this.viewerStateService.setViewerMode(newMode);

    this.markerService.clear3DMarkers();
    this.markerService.clear2DRectangles();

    setTimeout(() => {
      this.loadExistingNotes();
    }, 200);
  }

  private determineViewerMode(url: string | null): ViewerMode {
    if (!url) return 'NONE';

    const lowerUrl = url.toLowerCase();
    if (lowerUrl.endsWith('.gltf') || lowerUrl.endsWith('.glb')) return '3D';
    if (
      lowerUrl.endsWith('.png') ||
      lowerUrl.endsWith('.jpg') ||
      lowerUrl.endsWith('.jpeg') ||
      lowerUrl.endsWith('.webp') ||
      lowerUrl.endsWith('.bmp')
    )
      return '2D';

    return 'NONE';
  }

  onModelLoaded(): void {
    this.isLoading = false;
    this.loadExistingNotes();
  }

  onModelLoadError(error: Error): void {
    this.isLoading = false;
    this.hasError = true;
    console.error('Model load failed:', error);
  }

  resetView(): void {
    if (this.viewerMode === '3D' && this.viewer3D) {
      this.viewer3D.resetCamera();
    }
  }

  toggleControls(): void {
    this.showControls = !this.showControls;
  }

  toggleHelp(): void {
    this.showHelp = !this.showHelp;
  }

  updateBrightness(event: any): void {
    this.brightness = event.target.value;
    if (this.viewer3D) {
      this.viewer3D.setBrightness(this.brightness);
    }
  }

  toggleTextureMode(): void {
    this.isTextureMode = !this.isTextureMode;
    if (this.viewer3D) {
      this.viewer3D.setContrastMode(this.isTextureMode);
    }
  }

  toggleTexture(): void {
    this.showTexture = !this.showTexture;
    if (this.viewer3D) {
      this.viewer3D.setTextureEnabled(this.showTexture);
    }
  }

  private update3DMarkers(markers: MarkerData[]): void {
    if (!this.viewer3D) return;

    this.markers3D.forEach((marker) => this.viewer3D!.removeMarker(marker));
    this.markers3D = [];

    markers.forEach((markerData) => {
      const mesh = this.viewer3D!.addMarker(
        markerData.position,
        markerData.note,
      );
      this.markers3D.push(mesh);
    });

    this.updateMarkerCount();
  }

  on3DMarkerAdd(position: THREE.Vector3): void {
    this.pendingPosition = position;
    this.editingIndex = null;
    this.currentNote = '';
    this.isEditingMode = false;
    this.showNoteModal = true;
  }

  on3DMarkerClick(index: number): void {
    this.selectedMarkerId = this.markerService.get3DMarkers()[index].id;
    this.editingIndex = index;
    const markerData = this.markerService.get3DMarkers()[index];
    this.currentNote = markerData?.note || '';
    this.isEditingMode = true;
    // this.showNoteModal = true;
  }

  on2DRectangleAdd(rectangle: RectangleData): void {
    this.pendingRectangle = rectangle;
    this.editingIndex = null;
    this.currentNote = '';
    this.isEditingMode = false;
    this.showNoteModal = true;
  }

  on2DRectangleClick(index: number): void {
    this.selectedMarkerId = this.rectangles2D[index].id;
    this.editingIndex = index;
    this.currentNote = this.rectangles2D[index]?.note || '';
    this.isEditingMode = true;
    this.showNoteModal = true;
  }

  onNoteSave(note: string): void {
    const currentWoundId = this.existingNotes?.woundId;
    const currentFileName = this.existingNotes?.url;

    if (this.isEditingMode) {
      this.updateWoundModelNote(this.selectedMarkerId.toString(), note);
    } else {
      const payload: CreateWoundModelNoteRequest = {
        woundId: currentWoundId,
        note: note,
        fileName: currentFileName,
        markerType:
          this.viewerMode === '3D'
            ? MarkerType.ThreeDimensional
            : MarkerType.TwoDimensional,
        positionData: '',
      };

      if (this.viewerMode === '3D' && this.pendingPosition) {
        const positionWithNote = {
          x: this.pendingPosition.x,
          y: this.pendingPosition.y,
          z: this.pendingPosition.z,
        };
        payload.positionData = JSON.stringify(positionWithNote);

        this.woundService.createWoundModelNote(payload).subscribe(() => {
          this.onMarkerCreated.emit(payload);
        });
      } else if (this.viewerMode === '2D' && this.pendingRectangle) {
        payload.positionData = JSON.stringify(this.pendingRectangle);

        this.woundService.createWoundModelNote(payload).subscribe({
          next: (res) => {
            if (res.data && res.data.id) {
              this.markerService.add2DRectangle({
                ...this.pendingRectangle!,
                note: note,
                id: res.data.id,
              });
            } else {
              this.markerService.add2DRectangle({
                ...this.pendingRectangle!,
                note: note,
                id: Date.now().toString(),
              });
            }
            this.onMarkerCreated.emit(payload);
          },
          error: (err) => {
            console.error('Error saving 2D note:', err);
          },
        });
      }
    }
    this.closeNoteModal();
  }

  closeNoteModal(): void {
    this.showNoteModal = false;
    this.currentNote = '';
    this.editingIndex = null;
    this.pendingPosition = null;
    this.pendingRectangle = null;
    this.isEditingMode = false;
  }

  onNoteDeleteFromModal(): void {
    if (this.selectedMarkerId) {
      this.deleteWoundModelNote(this.selectedMarkerId.toString());
    }
  }

  onControlAction(action: ControlAction): void {
    switch (action.type) {
      case 'gizmo':
        this.viewerStateService.toggleGizmoMode();
        break;
      case 'marker':
        this.viewerStateService.toggleAddMarkerMode();
        break;
      case 'draw':
        this.viewerStateService.toggleDrawing2DMode();
        break;
      case 'select':
        this.viewerStateService.toggleSelect2DMode();
        break;
      case 'save':
        this.handleSave();
        break;
      case 'undo':
        this.handleUndo();
        break;
      case 'clear':
        this.handleClear();
        break;
      case 'delete':
        if (this.selectedMarkerId) {
          this.deleteWoundModelNote(this.selectedMarkerId.toString());
        }
        break;
      case 'update':
        if (this.selectedMarkerId) {
          this.showNoteModal = true;
        }
        break;
      case 'toggle':
        this.markersVisible = !this.markersVisible;
        if (this.viewer3D) {
          this.viewer3D.toggleMarkers(this.markersVisible);
        }
        break;
      case 'metrics':
        this.showMetricsPanel = !this.showMetricsPanel;
        break;
    }
  }

  onMeasurementAction(action: MeasurementAction): void {
    if (!this.showMeasurementSidebar) {
      this.clearMeasurementModes();
      this.lastMeasurementResult = null;
      return;
    }

    const tool = action.type;

    // Toggle: clicking same tool turns it off
    if (this.activeMeasurementTool === tool) {
      this.clearMeasurementModes();
      return;
    }

    this.clearMeasurementModes();

    switch (tool) {
      case 'contrast':
        this.toggleTextureMode();
        break;
      case 'texture':
        this.toggleTexture();
        break;
      case 'reset':
        this.resetView();
        break;
      case 'zoom-in':
        this.activeMeasurementTool = 'zoom-in';
        // Zoom in by moving camera closer
        if (this.viewer3D) {
          const cam = this.viewer3D.getCamera();
          const ctrl = (this.viewer3D as any).controls;
          if (cam && ctrl) {
            cam.position.lerp(ctrl.target, 0.2);
          }
        }
        setTimeout(() => { this.activeMeasurementTool = null; }, 300);
        break;
      case 'pan':
        this.activeMeasurementTool = 'pan';
        break;
      case 'measure-distance':
        this.activeMeasurementTool = 'measure-distance';
        this.isMeasureDistanceMode = true;
        // Disable other active modes
        this.viewerStateService.setAddMarkerMode(false);
        this.viewerStateService.setGizmoMode(false);
        break;
      case 'measure-angle':
        this.activeMeasurementTool = 'measure-angle';
        this.isMeasureAngleMode = true;
        this.viewerStateService.setAddMarkerMode(false);
        this.viewerStateService.setGizmoMode(false);
        break;
      case 'area':
        this.activeMeasurementTool = 'area';
        this.isMeasureAreaMode = true;
        this.viewerStateService.setAddMarkerMode(false);
        this.viewerStateService.setGizmoMode(false);
        break;
      case 'finalize-area':
        if (this.viewer3D) {
          this.viewer3D.finalizeMeasureArea();
        }
        break;
    }
  }

  clearMeasurementModes(): void {
    this.activeMeasurementTool = null;
    this.isMeasureDistanceMode = false;
    this.isMeasureAngleMode = false;
    this.isMeasureAreaMode = false;
    if (this.viewer3D) {
      this.viewer3D.clearMeasurements();
    }
  }

  onMeasurementResult(result: { type: string; value: number; unit: string; label: string }): void {
    this.lastMeasurementResult = result;
    // Auto-clear after showing result (allow next measurement)
    if (result.type !== 'area') {
      // Keep tool active for repeated measurements
    }
  }

  private handleSave(): void {
    if (this.viewerMode === '3D') {
      this.markerService.save3DMarkers(this.modelUrl);
      console.log('3D Markers saved');
    } else if (this.viewerMode === '2D') {
      // this.markerService.save2DRectangles(this.modelUrl);
      console.log('2D Rectangles saved');
    }
  }

  private handleUndo(): void {
    if (this.viewerMode === '3D') {
      this.markerService.removeLast3DMarker();
    } else if (this.viewerMode === '2D') {
      this.markerService.removeLast2DRectangle();
    }
  }

  private handleClear(): void {
    if (this.viewerMode === '3D') {
      this.markerService.clear3DMarkers();
      this.markerService.save3DMarkers(this.modelUrl);
    } else if (this.viewerMode === '2D') {
      this.markerService.clear2DRectangles();
      // this.markerService.save2DRectangles(this.modelUrl);
    }
  }

  @HostListener('window:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (this.showNoteModal) return;
    if ((event.target as HTMLElement).closest('input, textarea')) return;

    if (this.viewerMode === '3D' && this.viewer3D) {
      this.viewer3D.handleKeyDown(event);
    }
  }

  @HostListener('window:resize')
  onResize(): void {
    if (this.viewerMode === '3D' && this.viewer3D) {
      this.viewer3D.handleResize();
    }
  }

  @HostListener('mousemove', ['$event'])
  onMouseMove(event: MouseEvent): void {
    if (this.viewerMode !== '3D' || !this.viewer3D) {
      this.hoverText = null;
      this.hoveredMarkerNote = null;
      return;
    }

    const renderer = this.viewer3D.getRenderer();
    const model = this.viewer3D.getModel();
    const camera = this.viewer3D.getCamera();
    const raycaster = this.viewer3D.getRaycaster();
    const pointer = this.viewer3D.getPointer();

    if (!renderer || !model) return;

    const rect = renderer.domElement.getBoundingClientRect();

    if (
      event.clientX >= rect.left &&
      event.clientX <= rect.right &&
      event.clientY >= rect.top &&
      event.clientY <= rect.bottom
    ) {
      pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      pointer.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

      raycaster.setFromCamera(pointer, camera);
      const intersects = raycaster.intersectObjects(model.children, true);

      this.hoveredMarkerNote = null;

      if (intersects.length > 0) {
        const mesh = intersects[0].object as any;

        if (mesh.userData['isMarker'] && mesh.userData['note']) {
          this.hoveredMarkerNote = mesh.userData['note'];
          this.tooltipX = event.clientX + 10;
          this.tooltipY = event.clientY + 10;
          this.hoverText = null;
          return;
        }

        const tooltip = mesh.userData.tooltip;
        if (tooltip) {
          this.hoverText = tooltip;
          this.tooltipX = event.clientX + 10;
          this.tooltipY = event.clientY + 10;
          return;
        }
      }

      this.hoverText = null;
    } else {
      this.hoverText = null;
      this.hoveredMarkerNote = null;
    }
  }

  deleteWoundModelNote(id: string) {
    this.woundService.deleteWoundModelNote(id).subscribe((res) => {
      this.onMarkerDeleted.emit(res.data.isSuccess);
      this.selectedMarkerId = null;
      this.isEditingMode = false;
      this.showNoteModal = false;
    });
  }

  updateWoundModelNote(id: string, note: string) {
    this.woundService
      .updateWoundModelNote({
        id: id,
        note: note,
      })
      .subscribe((res) => {
        this.onMarkerUpdated.emit(res.data.isSuccess);
        this.selectedMarkerId = null;
        this.isEditingMode = false;
      });
  }

  private updateMarkerCount(): void {
    this.markerCount =
      this.viewerMode === '3D'
        ? this.markerService.getCount('3D')
        : this.markerService.getCount('2D');
  }
}
